<template>
  <base-button v-if="!inDialog && canUndo" :aria-label="$t.back" :class="brandClasses.back" @click="undo">
    <vf-icon dir="left" name="arrow" size="md" />
  </base-button>

  <vf-logo
    :alt="$feature.enableLoyaltyFeatures ? $t.loyaltyName : $t.brandName"
    class="mx-a h-a w-30"
    :class="brandClasses.logo"
    :invert="$feature.enableLoyaltyFeatures ? invertLoyaltyLogo : invertLogo"
    :variant="$feature.enableLoyaltyFeatures ? 'loyalty' : 'default'"
  />

  <form-simplified-enrollment-lookup
    v-if="enrollmentState === EnrollmentState.LOOKUP"
    :email
    :form-location
    @submit="handleLookup"
  >
    <template #heading>
      <slot name="lookup-heading" />
    </template>
  </form-simplified-enrollment-lookup>

  <template v-if="enrollmentState === EnrollmentState.SIGNIN_PASSWORD">
    <template v-if="$feature.enableLoyaltyFeatures">
      <h2 :class="brandClasses.heading">
        {{ $t.simplifiedEnrollment.signInHeading }}
      </h2>
      <p :class="brandClasses.description">
        {{ $t.simplifiedEnrollment.signInDescription }}
      </p>
    </template>
    <h3 :class="[brandClasses.subheading, brandClasses.signInPasswordSubheading]">
      {{ $t.signIn }}
    </h3>
    <form-sign-in
      compact
      disable-email
      :e-spot="belowSignInEspot"
      :email
      :form-location
      inside-dialog
      prevent-dialog
      @forgot-password="enrollmentState = EnrollmentState.FORGOT_PASSWORD"
      @submit="handleSignIn"
    />
  </template>

  <template v-if="enrollmentState === EnrollmentState.SIGNUP">
    <h2 v-if="$feature.enableLoyaltyFeatures || !signUpHeadingIsLoyaltyContent" :class="brandClasses.heading">
      {{ $t.simplifiedEnrollment.signUpHeading }}
    </h2>
    <div v-if="signUpShowEmail" class="flex mt-8!" :class="brandClasses.emailLine">
      {{ email }}
      <base-button
        class="ml-2 fw-bold underlined "
        :class="brandClasses.emailLineEditBtn"
        @click="enrollmentState = EnrollmentState.LOOKUP"
      >
        {{ $t.edit }}
      </base-button>
    </div>
    <p v-else-if="$feature.enableLoyaltyFeatures || !signUpHeadingIsLoyaltyContent" :class="brandClasses.description">
      {{ $t.simplifiedEnrollment.signUpDescription }}
    </p>
    <form-sign-up
      compact
      disable-email
      :e-spot="belowSignUpEspot"
      :email
      :form-location
      :simplified="enableSimplifiedForm"
      skip-dialog-interests
      @submit="handleSignUp"
    />
  </template>

  <form-forgot-password
    v-if="enrollmentState === EnrollmentState.FORGOT_PASSWORD"
    :email
    @submit="emit('resolve')"
  />

  <div v-if="enrollmentState === EnrollmentState.SIGNIN_OPTIONS">
    <h2 class="text-center" :class="brandClasses.heading">
      {{ $t.simplifiedEnrollment.signIntoYourAccount }}
    </h2>
    <div class="mt-8 flex" :class="brandClasses.emailLine">
      {{ email }}
      <base-button
        class="ml-2 fw-bold underlined "
        :class="brandClasses.emailLineEditBtn"
        @click="enrollmentState = EnrollmentState.LOOKUP"
      >
        {{ $t.edit }}
      </base-button>
    </div>
    <form-simplified-enrollment-sign-in-options
      v-if="lookupData"
      class="mt-6"
      :form-location
      :lookup-data
      @continue="(state) => enrollmentState = state"
      @resolve="emit('resolve')"
    />
  </div>

  <div v-if="enrollmentState === EnrollmentState.CREATE_PASSKEY" class="f-col grow">
    <h2 class="text-center" :class="brandClasses.heading">
      {{ $t.mfa.setUpNewPasskey }}
    </h2>
    <form-simplified-enrollment-create-passkey
      v-if="lookupData"
      class="mt-8 grow"
      :class="brandClasses.passkeyForm"
      :lookup-data
      @continue="(state) => { selectedOtpMethod = 'passkey'; enrollmentState = state; }"
    />
  </div>

  <div v-if="enrollmentState === EnrollmentState.VERIFICATION_OPTIONS" class="f-col grow">
    <h2 class="text-center" :class="brandClasses.heading">
      {{ $t.mfa.verificationOptionsHeading }}
    </h2>
    <div class="mb-6 mt-8" :class="brandClasses.emailLine">
      {{ email }}
      <base-button
        class="ml-2 fw-bold underlined "
        :class="brandClasses.emailLineEditBtn"
        @click="enrollmentState = EnrollmentState.LOOKUP"
      >
        {{ $t.edit }}
      </base-button>
    </div>
    <form-simplified-enrollment-verification-options
      v-if="lookupData"
      :lookup-data
      @continue="handleVerificationContinue"
      @try-another-way="enrollmentState = EnrollmentState.SIGNIN_OPTIONS"
    />
  </div>

  <template v-if="enrollmentState === EnrollmentState.OTP">
    <h2 class="text-center" :class="brandClasses.heading">
      {{ $t.mfa.otpHeading }}
    </h2>
    <div class="mb-4 f-col center text-center" :class="brandClasses.description">
      <template v-if="otpHeadingOneLine">
        <div class="body-2 lg:hidden">
          {{ replaceAll($t.mfa.weSentACodeToOneLineShort, {
            destination: selectedOtpMethod === 'sms' ? lookupData?.sms : lookupData?.email,
          }) }}
        </div>
        <div class="body-2 <lg:hidden">
          {{ replaceAll($t.mfa.weSentACodeToOneLine, {
            digits: lookupData?.otpLength,
            destination: selectedOtpMethod === 'sms' ? lookupData?.sms : lookupData?.email,
          }) }}
        </div>
      </template>
      <template v-else>
        <div>{{ $t.mfa.weSentACodeTo }}:</div>
        <div v-if="selectedOtpMethod === 'sms'" class="flex items-center">
          <vf-icon name="horizontal-ellipsis" size="lg" />
          <vf-icon name="horizontal-ellipsis" size="lg" />
          {{ lookupData?.sms }}
        </div>
        <div v-else>
          {{ lookupData?.email }}
        </div>
      </template>
    </div>

    <form-simplified-enrollment-otp-verification
      v-if="lookupData"
      :action="lookupData.success ? 'login' : 'signup'"
      :digits="lookupData.otpLength"
      :email="lookupData.email"
      :form-location
      :method="selectedOtpMethod"
      @continue="onContinueOtp"
      @resolve="emit('resolve')"
      @try-another-way="enrollmentState = EnrollmentState.SIGNIN_OPTIONS"
    />
  </template>

  <form-simplified-enrollment-non-loyalty
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_NON_LOYALTY"
    @cancel="emit('resolve')"
    @continue="enrollmentState = EnrollmentState.COMPLETE_PROFILE_WELCOME"
  />

  <form-simplified-enrollment-welcome v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_OTP_WELCOME">
    <template #description>
      <template v-if="subscribedToSms">
        {{ replaceAll($t.simplifiedEnrollment.signUpRewardSmsSubscriptionOptIn, {
          percent: $feature.configSignUp?.loyaltyRewardPercent ?? 10,
        }) }}
      </template>
      <template v-else>
        <div class="fw-bold -mt-2">
          {{ $t.simplifiedEnrollment.signUpRewardSmsSubscriptionOptOut }}
        </div>
        <base-link class="underscored fw-bold" target="_blank" to="/loyalty-terms">
          {{ $t.seeTerms }}
        </base-link>
      </template>
    </template>
    <template #actions>
      <vf-button
        class="w-full"
        to="/account/profile"
        @click="$gtm.push(
          'authForm.onRegistrationConfirmed', 'Setup Account', formLocation, 'loyalty', 'Standard', true,
        )"
      >
        {{ $t.simplifiedEnrollment.signUpRewardCompleteProfile }}
      </vf-button>
      <vf-button class="w-full capitalize" variant="secondary" @click="emit('resolve')">
        {{ $t.continueShopping }}
      </vf-button>
    </template>
  </form-simplified-enrollment-welcome>

  <form-simplified-enrollment-welcome
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_WELCOME"
    @cancel="() => $feature.tempEnablePostAuthOnboarding
      ? checkNextCompletionStep() : emit('resolve')"
    @continue="checkNextCompletionStep"
  />

  <form-simplified-enrollment-mobile
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_MOBILE"
    @continue="checkNextCompletionStep"
  />

  <form-simplified-enrollment-subscription
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_SUBSCRIPTION"
    :form-location
    :mode="subscriptionMode"
    @continue="checkStepSubscription"
  />

  <form-simplified-enrollment-subscription
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_OTP_SUBSCRIPTION"
    :email-opt-in-required="false"
    form-class="o:mt-0"
    :form-location
    :mobile-opt-in-hint="false"
    :mode="subscriptionMode"
    @continue="handleOtpSignUpSubscription($event)"
  >
    <template #progress-bar>
      <span />
    </template>
    <template #heading>
      <h2 :class="[brandClasses.heading, brandClasses.otpSubscriptionHeading]">
        {{ $t.simplifiedEnrollment.signUpHeading }}
      </h2>
      <p :class="[brandClasses.description, brandClasses.otpSubscriptionDescription]">
        {{ replaceAll($t.simplifiedEnrollment.signUpRewardDescription, {
          percent: $feature.configSignUp?.loyaltyRewardPercent ?? 10,
        }) }}
      </p>
    </template>
    <template #above-email>
      <h4 class="b-brand-7 pt-4 body-1 fw-bold" :class="{ 'b-t mt-4': subscriptionMode === 'both' }">
        {{ $t.simplifiedEnrollment.signUpRewardEmailSubscriptionHeading }}
      </h4>
    </template>
    <template #email-label>
      <form-legal-disclaimer :content="$t.simplifiedEnrollment.signUpRewardEmailSubscriptionLabel" />
    </template>
    <template #actions="{ form, loading, skip }">
      <vf-button class="w-full" :disabled="!form.phone || !form.smsAccepted" :loading type="submit">
        {{ subscriptionMode === 'email' ? $t.next : replaceAll($t.simplifiedEnrollment.signUpRewardSubmit, {
          percent: $feature.configSignUp?.loyaltyRewardPercent ?? 10,
        }) }}
      </vf-button>
      <div v-if="subscriptionMode !== 'email'" class="text-center">
        <base-link class="underscored text-sm" :disabled="loading" @click="skip">
          {{ replaceAll($t.simplifiedEnrollment.signUpRewardSkip, {
            percent: $feature.configSignUp?.loyaltyRewardPercent ?? 10,
          }) }}
        </base-link>
      </div>
    </template>
  </form-simplified-enrollment-subscription>

  <form-simplified-enrollment-profile-information
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_PROFILE"
    @continue="onContinueProfile"
  />

  <form-simplified-enrollment-interests
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_INTERESTS"
    :interests="user.interests"
    @continue="onContinueInterests"
  />

  <form-simplified-enrollment-success
    v-if="enrollmentState === EnrollmentState.COMPLETE_PROFILE_SUCCESS
      && (!$feature.tempEnablePostAuthOnboarding || showCompletionStep)"
    @cancel="emit('resolve')"
    @continue="onContinueSuccess"
  />
</template>

<script lang="ts" setup>
import type { MfaPayload } from '#root/api/clients/authentication/data-contracts'
import type { LookupData } from '#root/api/clients/consumer/data-contracts'
import type { ContentPagePageItems } from '#root/api/clients/content/data-contracts'
import type { AccountGatewayEntryPoint, FormLocation } from '#types/gtm'
import { NotificationConfigContextKey } from '#brands/components/vf/notification/context'
import { EnrollmentState } from '#root/enums/profile'

type FlowSource = 'register' | 'signin'
export type InitialLookup = {
  email: string
  response?: LookupData
}
export type Props = {
  email?: string
  entryPoint?: AccountGatewayEntryPoint
  initialLookup?: InitialLookup
  inDialog?: boolean
  formLocation: FormLocation
  autoStartPostAuth?: FlowSource
}
export type Emits = { resolve: [v?: boolean] }

const { email: emailProp = '', entryPoint = 'Sign In/Sign Up', formLocation, initialLookup, autoStartPostAuth } = defineProps<Props>()
const emit = defineEmits<Emits>()

const appConfig = useAppConfig()
const {
  brandClasses,
  brandStyles,
  enableSimplifiedForm,
  invertLogo,
  invertLoyaltyLogo,
  otpHeadingOneLine,
  showInterestsStep,
  signUpHeadingIsLoyaltyContent,
  signUpShowEmail,
} = appConfig.components.dialog.simplifiedEnrollment

const { $feature, $gtm } = useNuxtApp()
const auth = useAuthStore()
const user = useUserStore()
const { smsSubscription } = useSubscriptions()
const { getSections } = useCms()
const timelyDismissal = useTimelyDismissal()

const getLookupEnrollmentState = (lookupResponse?: LookupData): EnrollmentState => {
  if (!lookupResponse || !lookupResponse.success) {
    if (lookupResponse?.authMethods?.length === 1 && lookupResponse.authMethods[0] === 'mfa')
      return EnrollmentState.OTP

    return EnrollmentState.SIGNUP
  }

  if (lookupResponse.authMethods.length > 1)
    return EnrollmentState.SIGNIN_OPTIONS

  switch (lookupResponse.authMethods[0]) {
    case 'password':
      return EnrollmentState.SIGNIN_PASSWORD
    case 'mfa':
      return EnrollmentState.VERIFICATION_OPTIONS
    case 'passkey':
      return EnrollmentState.SIGNIN_OPTIONS
  }

  return EnrollmentState.SIGNIN_OPTIONS
}

const email = ref(initialLookup?.email ?? emailProp)
const lookupData = ref<LookupData | undefined>(initialLookup?.response)
const enrollmentState = ref(initialLookup
  ? getLookupEnrollmentState(initialLookup.response)
  : user.getInitialEnrollmentState())
const selectedOtpMethod = ref<MfaPayload['authMethod']>('email')
const belowSignInEspot = ref<ContentPagePageItems>()
const belowSignUpEspot = ref<ContentPagePageItems>()
const currentFlowSource = ref<FlowSource>('signin')
const showCompletionStep = ref(false)
const subscribedToSms = ref(false)

const redirectTo = useRouteQuery<string | undefined>('redirectTo')
const { undo: undoState, canUndo: canUndoState, clear, dispose } = useRefHistory(enrollmentState)

const DISMISS_IDS = {
  SUBSCRIPTION: 'se_subscription',
  PROFILE: 'se_profile',
  INTERESTS: 'se_interests',
} as const

const FLOW_ORDER: readonly EnrollmentState[] = [
  EnrollmentState.COMPLETE_PROFILE_SUBSCRIPTION,
  EnrollmentState.COMPLETE_PROFILE_PROFILE,
  EnrollmentState.COMPLETE_PROFILE_INTERESTS,
  EnrollmentState.COMPLETE_PROFILE_SUCCESS,
]

const NON_UNDO_STATES = computed(() => [
  EnrollmentState.COMPLETE_PROFILE_OTP_SUBSCRIPTION,
  ...(subscribedToSms.value ? [EnrollmentState.COMPLETE_PROFILE_OTP_WELCOME] : []),
])

const getCookieKey = (id: string) => `${id}_${auth.consumerNo}`

const canUndo = computed(() => canUndoState.value && !NON_UNDO_STATES.value.includes(enrollmentState.value))

const undo = () => {
  if (enrollmentState.value === EnrollmentState.OTP && lookupData.value && !lookupData.value.success)
    $gtm.push('authForm.onRegistrationClose', formLocation, 'ecom', 'Back', 'OTP')

  undoState()
}

const subscriptionMode = computed(() => {
  if (!user.hasNewsletterSubscription && !smsSubscription.value?.optin) return 'both'
  if (user.hasNewsletterSubscription && !smsSubscription.value?.optin) return 'sms'
  if (!user.hasNewsletterSubscription && smsSubscription.value?.optin) return 'email'
  return undefined
})

const matchesRules = (step: EnrollmentState): boolean => {
  switch (step) {
    case EnrollmentState.COMPLETE_PROFILE_SUBSCRIPTION:
      return $feature.configOnboardingSteps.subscriptions
        && (!user.hasNewsletterSubscription || !smsSubscription.value?.optin)
    case EnrollmentState.COMPLETE_PROFILE_PROFILE:
      return $feature.configOnboardingSteps.profile && !!user.isPartiallyEnrolled
    case EnrollmentState.COMPLETE_PROFILE_INTERESTS:
      return $feature.configOnboardingSteps.interests && !user.interests.length
    case EnrollmentState.COMPLETE_PROFILE_SUCCESS:
      return $feature.configOnboardingSteps.success
    default:
      return false
  }
}

const canShowStep = (step: EnrollmentState, source: FlowSource): boolean => {
  if (!matchesRules(step)) return false
  if (source === 'register') return true
  switch (step) {
    case EnrollmentState.COMPLETE_PROFILE_SUBSCRIPTION:
      return timelyDismissal.check(getCookieKey(DISMISS_IDS.SUBSCRIPTION))
    case EnrollmentState.COMPLETE_PROFILE_PROFILE:
      return timelyDismissal.check(getCookieKey(DISMISS_IDS.PROFILE))
    case EnrollmentState.COMPLETE_PROFILE_INTERESTS:
      return timelyDismissal.check(getCookieKey(DISMISS_IDS.INTERESTS))
    default:
      return true
  }
}

const nextEligibleStep = (source: FlowSource, after?: EnrollmentState): EnrollmentState => {
  const start = after ? Math.max(0, FLOW_ORDER.indexOf(after) + 1) : 0
  for (let i = start; i < FLOW_ORDER.length; i++) {
    const step = FLOW_ORDER[i]
    if (canShowStep(step, source)) return step
  }
  return EnrollmentState.COMPLETE_PROFILE_SUCCESS
}

const postAuthSequence = (source: FlowSource, after?: EnrollmentState) => {
  currentFlowSource.value = source
  const next = nextEligibleStep(source, after)
  if (next === EnrollmentState.COMPLETE_PROFILE_SUCCESS) {
    if (showCompletionStep.value && (source !== 'signin' || $feature.showCompleteProfileFormAfterSignIn)) {
      enrollmentState.value = next
    }
    else if (!showCompletionStep.value || !$feature.showCompleteProfileFormAfterSignIn) {
      emit('resolve')
    }
  }
  else {
    showCompletionStep.value = true
    enrollmentState.value = next
  }
}

const routeToNextFrom = (current: EnrollmentState, source: FlowSource) => postAuthSequence(source, current)

const handleLookup = (lookupEmail: string, lookupResponse?: LookupData) => {
  email.value = lookupEmail
  lookupData.value = lookupResponse
  enrollmentState.value = getLookupEnrollmentState(lookupResponse)
}

const handleVerificationContinue = (method: MfaPayload['authMethod']) => {
  selectedOtpMethod.value = method
  enrollmentState.value = EnrollmentState.OTP
}

const checkStepSuccessOrWelcome = () => {
  if ($feature.tempEnablePostAuthOnboarding) {
    if (user.hasNewsletterSubscription && smsSubscription.value?.optin
      && !user.isPartiallyEnrolled && user.interests.length > 0) {
      emit('resolve')
    }

    enrollmentState.value = user.getInitialEnrollmentState()
    nextTick(() => {
      clear()
    })
  }
  else {
    if (!user.isPartiallyEnrolled)
      emit('resolve')

    enrollmentState.value = user.getInitialEnrollmentState()
  }
}

const checkStepProfile = (submitted?: boolean) => {
  $gtm.push('authForm.onLoyaltyStep', 'Setup:Step2:nameZipCodeBirthday', submitted
    ? 'Submit'
    : 'Skipped', formLocation)
  if ($feature.tempEnablePostAuthOnboarding) {
    routeToNextFrom(EnrollmentState.COMPLETE_PROFILE_PROFILE, currentFlowSource.value)
  }
  else {
    enrollmentState.value = showInterestsStep && user.interests.length < 3
      ? EnrollmentState.COMPLETE_PROFILE_INTERESTS
      : EnrollmentState.COMPLETE_PROFILE_SUCCESS
  }
}

const checkStepSubscription = (submitted?: boolean) => {
  $gtm.push('authForm.onLoyaltyStep', 'Setup:Step1:phoneNumber', submitted ? 'Submit' : 'Skipped', formLocation)
  routeToNextFrom(EnrollmentState.COMPLETE_PROFILE_SUBSCRIPTION, currentFlowSource.value)
}

const checkStepInterests = (submitted?: boolean) => {
  $gtm.push('authForm.onLoyaltyStep', 'Setup:Step3:interests', submitted ? 'Submit' : 'Skipped', formLocation)
  routeToNextFrom(EnrollmentState.COMPLETE_PROFILE_INTERESTS, currentFlowSource.value)
  nextTick(() => {
    $gtm.push('authForm.onLoyaltySubmit', user.isPartiallyEnrolled
      ? 'Setup:noPoints'
      : 'Setup:yesPoints', formLocation)
  })
}

const checkStepWelcome = () => {
  $gtm.push('authForm.onRegistrationConfirmed', 'Setup Account', formLocation, 'loyalty', 'Standard', true)

  checkStepSuccessOrWelcome()
}

const checkStepMobile = (submitted?: boolean) => {
  $gtm.push('authForm.onLoyaltyStep', 'Setup:Step1:phoneNumber', submitted ? 'Submit' : 'Skipped', formLocation)

  if (!user.isPartiallyEnrolled)
    return checkStepProfile()

  enrollmentState.value = EnrollmentState.COMPLETE_PROFILE_PROFILE
}

const handleSignUp = () => {
  if ($feature.tempEnablePostAuthOnboarding) {
    postAuthSequence('register')
    dispose()
  }
  else {
    enrollmentState.value = EnrollmentState.COMPLETE_PROFILE_WELCOME
  }
}

const handleOtpSignUp = () => {
  if (subscriptionMode.value)
    enrollmentState.value = EnrollmentState.COMPLETE_PROFILE_OTP_SUBSCRIPTION
  else
    enrollmentState.value = EnrollmentState.COMPLETE_PROFILE_OTP_WELCOME
}

const handleOtpSignUpSubscription = (subscribed: boolean) => {
  subscribedToSms.value = subscribed
  enrollmentState.value = EnrollmentState.COMPLETE_PROFILE_OTP_WELCOME
}

const handleSignIn = () => {
  if ($feature.tempEnablePostAuthOnboarding) {
    postAuthSequence('signin')
    dispose()
  }
  else {
    if ($feature.showLoyaltyEnrollmentAfterSignIn && !user.profile?.enrollments.some(({ type }) => type === 'Loyalty'))
      enrollmentState.value = EnrollmentState.COMPLETE_PROFILE_NON_LOYALTY
    else if ($feature.showCompleteProfileFormAfterSignIn && user.isPartiallyEnrolled)
      enrollmentState.value = EnrollmentState.COMPLETE_PROFILE_WELCOME
    else
      emit('resolve')
  }
}

const checkNextCompletionStep = (submitted?: boolean) => {
  switch (enrollmentState.value) {
    case EnrollmentState.COMPLETE_PROFILE_WELCOME:
      checkStepWelcome()
      break
    case EnrollmentState.COMPLETE_PROFILE_MOBILE:
      checkStepMobile(submitted)
      break
    case EnrollmentState.COMPLETE_PROFILE_PROFILE:
      checkStepProfile(submitted)
      break
    case EnrollmentState.COMPLETE_PROFILE_INTERESTS:
      checkStepInterests(submitted)
      break
    case EnrollmentState.COMPLETE_PROFILE_SUCCESS:
      checkStepSuccessOrWelcome()
      break
  }
}

const onContinueOtp = () => {
  if (lookupData.value && !lookupData.value.success) {
    handleOtpSignUp()
  }
  else if ($feature.tempEnablePostAuthOnboarding) {
    handleSignIn()
  }
  else {
    enrollmentState.value = user.getInitialEnrollmentState()
    if (enrollmentState.value === EnrollmentState.COMPLETE_PROFILE_SUCCESS) {
      if (!showCompletionStep.value || !$feature.showCompleteProfileFormAfterSignIn) {
        emit('resolve')
      }
    }
  }
}

const onContinueProfile = () => {
  $feature.tempEnablePostAuthOnboarding ? checkStepProfile() : checkNextCompletionStep()
}

const onContinueInterests = () => {
  $feature.tempEnablePostAuthOnboarding ? checkStepInterests() : checkNextCompletionStep()
}

const onContinueSuccess = () => {
  $feature.tempEnablePostAuthOnboarding ? checkStepSuccessOrWelcome() : checkNextCompletionStep()
}

const fetchEspots = async () => {
  const [signInResult, signUpResult] = await Promise.allSettled([
    getSections.signInModal(),
    getSections.signUpModal()
  ])

  if (signInResult.status === 'fulfilled')
    belowSignInEspot.value = signInResult.value.data.value?.content.sectionsMap?.['below-sign-in']

  if (signUpResult.status === 'fulfilled')
    belowSignUpEspot.value = signUpResult.value.data.value?.content.sectionsMap?.['below-sign-up']
}

onMounted(() => {
  $gtm.push('authForm.onImpression', 'Login', formLocation)
  $gtm.push('authForm.onImpression', 'Gateway', formLocation)
  $gtm.push('authForm.onEntry', entryPoint, formLocation)

  if (!initialLookup)
    enrollmentState.value = user.getInitialEnrollmentState()

  fetchEspots()
  if (autoStartPostAuth) {
    nextTick(() => {
      clear()
      postAuthSequence(autoStartPostAuth)
    })
  }
})

onBeforeUnmount(() => {
  dispose()
  const { isSafe, redirect } = useSafeRedirect()
  if (auth.loggedIn && redirectTo.value && isSafe(redirectTo.value)) {
    redirect(redirectTo.value)
    return
  }
  else if (!auth.loggedIn) {
    switch (enrollmentState.value) {
      case EnrollmentState.LOOKUP:
        break
      case EnrollmentState.CREATE_PASSKEY:
      case EnrollmentState.FORGOT_PASSWORD:
      case EnrollmentState.OTP:
      case EnrollmentState.SIGNIN_PASSWORD:
      case EnrollmentState.SIGNIN_OPTIONS:
      case EnrollmentState.VERIFICATION_OPTIONS:
        $gtm.push('authForm.onLoginClose', formLocation)
        break
      default:
        $gtm.push('authForm.onRegistrationClose', formLocation, 'ecom + loyalty')
        break
    }

    $gtm.push('authForm.onLoginClose', formLocation, 'Gateway')
  }
  else {
    switch (enrollmentState.value) {
      case EnrollmentState.COMPLETE_PROFILE_OTP_SUBSCRIPTION:
        $gtm.push('authForm.onMarketingAction', 'Registration', 'MarketingOptIn Exit', 'OTP', 'ecom', formLocation)
        break
    }
  }
  enrollmentState.value = user.getInitialEnrollmentState()
})

provide(NotificationConfigContextKey, {
  brandClasses: brandClasses.notification,
  brandStyles: brandStyles?.notification
})

defineExpose({
  canUndo,
  enrollmentState,
  undo,
  setEnrollmentState: (state: EnrollmentState) => enrollmentState.value = state,
})
</script>
